import React from 'react';
import PropTypes from 'prop-types';
import Platform from "react-native-web/dist/exports/Platform";
import StyleSheet from "react-native-web/dist/exports/StyleSheet";
import View from "react-native-web/dist/exports/View";
import ProgressBarBackground from './ProgressBarBackground';
import { applyShadowToken, useThemeTokens } from '../ThemeProvider';
import { a11yProps, getTokensPropType, selectSystemProps, variantProp, viewProps } from '../utils';
import { MAX_PERCENT_VALUE, MIN_PERCENT_VALUE } from './constants';
import { jsx as _jsx } from "react/jsx-runtime";
const [selectProps, selectedSystemPropTypes] = selectSystemProps([a11yProps, viewProps]);
const selectBarStyles = (_ref, calculatedPercentage, barPosition) => {
  let {
    backgroundColor,
    borderRadius,
    outlineWidth,
    outlineColor,
    shadow
  } = _ref;
  return {
    backgroundColor,
    borderRadius,
    outlineWidth,
    outlineColor,
    ...applyShadowToken(shadow),
    width: `${calculatedPercentage}%`,
    left: `${barPosition}%`
  };
};

/**
 * The `ProgressBar` is a visual representation of linear progression.
 * It provides simple but important information at a quick glance.
 *
 * ## Props
 *
 * - Use `percentage` prop to indicate completion
 *
 * ## Tokens
 *
 * - Use the following tokens to customize the appearance:
 *   - `backgroundColor` for the background color of the progress bar,
 *   - `borderRadius` for the rounded corners,
 *   - `outlineColor` to control the color of the border (outline),
 *   - `outlineWidth` to change the outline width.
 *   - `shadow` to apply a box shadow to the progress bar.
 *
 * ## Variants
 *
 * - Use the following variants to render specific types of progress bars:
 *   - `negative`: set to `true` if you wish to use the negative theming for progress bar filling,
 *   - `inactive`: set to `true` if you wish to style the progress bar as inactive.
 *
 * ## Usability and A11y guidelines
 *
 * `ProgressBar` component accepts all the standard accessibility properties including the ones that
 * are specific to the `progressbar` role. By default the following props are used:
 *
 * - `accessibilityRole` (`role`): `progressbar`,
 * - `accessibilityValue.min` (`aria-valuemin`): `0`,
 * - `accessibilityValue.max` (`aria-valuemax`): `100`,
 * - `accessibilityValue.now` (`aria-valuenow`): `percentage`,
 * - `accessibilityValue.text` (`aria-valuetext`): `%{percentage}%`,
 *
 */
const ProgressBar = /*#__PURE__*/React.forwardRef((_ref2, ref) => {
  let {
    children = null,
    percentage = 0,
    tokens,
    variant,
    offset = {
      items: 0,
      current: 1
    },
    ...rest
  } = _ref2;
  const {
    items,
    current
  } = offset;
  let calculatedPercentage = percentage;
  let barPosition = MIN_PERCENT_VALUE;
  const validItems = items > MIN_PERCENT_VALUE;
  if (validItems) {
    calculatedPercentage = MAX_PERCENT_VALUE / items;
    barPosition = calculatedPercentage * (current < 1 ? MIN_PERCENT_VALUE : current - 1);
    if (current > items) {
      barPosition = calculatedPercentage * (items - 1);
    }
  }
  const themeTokens = useThemeTokens('ProgressBar', tokens, variant);
  if (percentage < MIN_PERCENT_VALUE) {
    throw new Error('ProgressBar: `percentage` must be a positive number');
  } else if (percentage > MAX_PERCENT_VALUE) {
    throw new Error('ProgressBar: `percentage` cannot exceed 100');
  }
  const selectedProps = selectProps({
    accessibilityRole: 'progressbar',
    accessibilityValue: {
      min: MIN_PERCENT_VALUE,
      max: MAX_PERCENT_VALUE,
      now: calculatedPercentage.toFixed(2),
      text: `${validItems ? (barPosition + calculatedPercentage).toFixed(2) : calculatedPercentage}%`
    },
    ...rest
  });
  return percentage > MIN_PERCENT_VALUE || items ? /*#__PURE__*/_jsx(View, {
    ref: ref,
    style: [staticStyles.bar, selectBarStyles(themeTokens, calculatedPercentage, barPosition)],
    ...selectedProps,
    children: children ?? /*#__PURE__*/_jsx(ProgressBarBackground, {
      variant: variant
    })
  }) : null;
});
ProgressBar.displayName = 'ProgressBar';
ProgressBar.propTypes = {
  ...selectedSystemPropTypes,
  /**
   * Optional children that can be used to customize the progress bar filling.
   */
  children: PropTypes.node,
  /**
   * Accessibility label for the progress bar.
   */
  accessibilityLabel: PropTypes.string.isRequired,
  /**
   * Percentage of completion.
   */
  percentage: PropTypes.number,
  /**
   * Progress bar tokens.
   */
  tokens: getTokensPropType('ProgressBar'),
  /**
   * Progress bar variant.
   */
  variant: variantProp.propType,
  /**
   * Using the offset object, the `items` will determine the total number of items.
   * The `current` will determine the current position of the progress bar.
   */
  offset: PropTypes.shape({
    items: PropTypes.number,
    current: PropTypes.number
  })
};
export default ProgressBar;
const staticStyles = StyleSheet.create({
  bar: {
    height: '100%',
    overflow: 'hidden',
    position: 'relative',
    ...Platform.select({
      web: {
        outlineStyle: 'solid'
      }
    })
  }
});